import { useCallback, useEffect, useState } from 'react';
import { Button, Input, Modal } from '@cuito/ui';
import {
  LOCATION_TYPES,
  STOCK_TRANSFER_ROUTES,
  type LocationType,
  type ProductDto,
  type StockTransferRoute,
  type TransferDestination,
  type TransferStatus,
} from '@cuito/types';
import { catalogApi } from '../api/catalog';
import {
  stockApi,
  type LocationView,
  type StockSummaryRow,
  type TransferView,
} from '../api/stock';
import { readServerMessage } from '../lib/apiError';
import { useAuthStore } from '../stores/authStore';

/** 地点类型显示词（`05-ui-design-system.md` §7.2） */
const LOCATION_TYPE_LABELS: Record<LocationType, string> = {
  WAREHOUSE: '仓库',
  SHOP: '门店',
  EVENT: '展会',
  CONSIGN_HOLD: '寄售暂存',
};

/** 四类路线显示词（§7.3「仓→店 / 仓→展 / 展→展 / 社团→社团」） */
const ROUTE_LABELS: Record<StockTransferRoute, string> = {
  WAREHOUSE_TO_SHOP: '仓→店',
  WAREHOUSE_TO_EVENT: '仓→展',
  EVENT_TO_EVENT: '展→展',
  EVENT_TO_WAREHOUSE: '展→仓',
  TENANT_TO_TENANT: '社团→社团',
};

/** 调拨单状态显示词（§7.3：待发运 / 在途 / 已接收 / 已取消） */
const TRANSFER_STATUS_LABELS: Record<TransferStatus, string> = {
  DRAFT: '待发运',
  IN_TRANSIT: '在途',
  RECEIVED: '已接收',
  CANCELLED: '已取消',
};

/**
 * `GET /stock/summary` 的 `type` 出网是 `string`，先收窄再查表。
 *
 * 用类型守卫而不是把 `string` 强转成 `LocationType`：强转会在服务端将来新增
 * 地点类型时**静默显示 undefined**，收窄则会退回服务端原文。
 */
function isLocationType(value: string): value is LocationType {
  return LOCATION_TYPES.some((known) => known === value);
}

function locationTypeLabel(type: string): string {
  return isLocationType(type) ? LOCATION_TYPE_LABELS[type] : type;
}

/**
 * 路线 → 可选的地点类型，只用来**收窄下拉选项**。
 *
 * 权威校验在服务端（`TRANSFER_INVALID_ROUTE`）：这张表是「让用户少选错一次」的
 * 便利，不是门禁。`TENANT_TO_TENANT` 的目标是**对方社团**的地点，
 * `GET /locations` 按租户收窄拿不到，故目标改成手填地点 ID（见下方表单）。
 */
const ROUTE_LOCATION_TYPES: Record<StockTransferRoute, { source: LocationType[]; target: LocationType[] }> = {
  WAREHOUSE_TO_SHOP: { source: ['WAREHOUSE'], target: ['SHOP'] },
  WAREHOUSE_TO_EVENT: { source: ['WAREHOUSE'], target: ['EVENT'] },
  EVENT_TO_EVENT: { source: ['EVENT'], target: ['EVENT'] },
  EVENT_TO_WAREHOUSE: { source: ['EVENT'], target: ['WAREHOUSE'] },
  TENANT_TO_TENANT: {
    source: ['WAREHOUSE', 'SHOP', 'EVENT', 'CONSIGN_HOLD'],
    target: ['SHOP', 'EVENT'],
  },
};

interface TransferLineDraft {
  productId: string;
  quantity: number;
}

const EMPTY_LINE: TransferLineDraft = { productId: '', quantity: 1 };

/**
 * 库存管理（`05-ui-design-system.md` §3.2 `/stock`）。
 *
 * 三个区块：**库存概览**（按地点聚合）、**库存调整**（正数入库 / 负数出库）、
 * **库存调拨**（四类路线 + 按状态给可用动作）。
 *
 * 所有「能不能做」的判断都留给服务端：本页不预判「出库会不会把库存做成负数」，
 * 也不预判「这个仓库能不能删」——把后端带业务码的 `message` 原样展示出来，
 * 用户看到的才是真正的原因（可用量不足时它带具体件数）。
 */
export default function StockPage() {
  const canTransfer = useAuthStore((s) => s.hasPermission('stock:transfer'));

  const [locations, setLocations] = useState<LocationView[]>([]);
  const [products, setProducts] = useState<ProductDto[]>([]);
  const [summary, setSummary] = useState<StockSummaryRow[]>([]);
  const [transfers, setTransfers] = useState<TransferView[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // ── 库存调整 ─────────────────────────────────────────────
  const [adjustLocationId, setAdjustLocationId] = useState('');
  const [adjustProductId, setAdjustProductId] = useState('');
  const [adjustQuantity, setAdjustQuantity] = useState('');
  const [adjustReason, setAdjustReason] = useState('');
  const [currentAvailable, setCurrentAvailable] = useState<number | null>(null);
  const [adjusting, setAdjusting] = useState(false);
  const [adjustError, setAdjustError] = useState<string | null>(null);

  // ── 新建调拨 ─────────────────────────────────────────────
  const [showTransferForm, setShowTransferForm] = useState(false);
  const [route, setRoute] = useState<StockTransferRoute>('WAREHOUSE_TO_SHOP');
  const [sourceLocationId, setSourceLocationId] = useState('');
  const [targetLocationId, setTargetLocationId] = useState('');
  const [transferDestination, setTransferDestination] =
    useState<TransferDestination>('RETURN_TO_WAREHOUSE');
  const [lines, setLines] = useState<TransferLineDraft[]>([{ ...EMPTY_LINE }]);
  const [transferError, setTransferError] = useState<string | null>(null);
  const [savingTransfer, setSavingTransfer] = useState(false);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const [locationRows, summaryRows, transferPage] = await Promise.all([
        stockApi.listLocations(),
        stockApi.summary(),
        stockApi.listTransfers(),
      ]);
      setLocations(locationRows);
      setSummary(summaryRows);
      setTransfers(transferPage.items);
    } catch {
      setError('加载失败，请重试');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /** 调拨明细的商品下拉：与库存调整共用同一份本社团商品 */
  useEffect(() => {
    void (async () => {
      try {
        const res = await catalogApi.listProducts({ page: 1, pageSize: 100 });
        setProducts(res.items);
      } catch {
        setProducts([]);
      }
    })();
  }, []);

  /**
   * 调整区块里显示「当前可用」。
   *
   * 只是给用户一个填数量的依据，**不作为能否提交的判断**：真正的原子性与
   * 「调整后不能为负」由服务端的条件 UPDATE 保证（`REQ-STOCK-002`）。
   */
  useEffect(() => {
    if (adjustLocationId === '' || adjustProductId === '') {
      setCurrentAvailable(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const rows = await stockApi.listStockItems({
          locationId: adjustLocationId,
          productId: adjustProductId,
        });
        if (!cancelled) setCurrentAvailable(rows[0]?.availableQty ?? 0);
      } catch {
        if (!cancelled) setCurrentAvailable(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [adjustLocationId, adjustProductId]);

  const quantity = adjustQuantity.trim() === '' ? 0 : Number(adjustQuantity);
  const canAdjust =
    canTransfer &&
    adjustLocationId !== '' &&
    adjustProductId !== '' &&
    Number.isInteger(quantity) &&
    quantity !== 0 &&
    adjustReason.trim().length >= 2;

  const submitAdjust = async (): Promise<void> => {
    setAdjusting(true);
    setAdjustError(null);
    setNotice(null);
    try {
      const result = await stockApi.adjust({
        locationId: adjustLocationId,
        productId: adjustProductId,
        quantity,
        reason: adjustReason.trim(),
      });
      setNotice(`调整成功，当前可用 ${result.availableQty} 件`);
      setAdjustQuantity('');
      setAdjustReason('');
      setCurrentAvailable(result.availableQty);
      const summaryRows = await stockApi.summary();
      setSummary(summaryRows);
    } catch (e) {
      setAdjustError(readServerMessage(e) ?? '调整失败，请重试');
    } finally {
      setAdjusting(false);
    }
  };

  const openTransferForm = (): void => {
    setRoute('WAREHOUSE_TO_SHOP');
    setSourceLocationId('');
    setTargetLocationId('');
    setTransferDestination('RETURN_TO_WAREHOUSE');
    setLines([{ ...EMPTY_LINE }]);
    setTransferError(null);
    setShowTransferForm(true);
  };

  const submitTransfer = async (): Promise<void> => {
    const filled = lines.filter((line) => line.productId !== '' && line.quantity > 0);
    if (sourceLocationId === '') {
      setTransferError('请选择源地点');
      return;
    }
    if (targetLocationId.trim() === '') {
      setTransferError(
        route === 'TENANT_TO_TENANT' ? '请填写对方社团的地点 ID' : '请选择目标地点',
      );
      return;
    }
    if (filled.length === 0) {
      setTransferError('请至少添加一个商品');
      return;
    }

    setSavingTransfer(true);
    setTransferError(null);
    try {
      await stockApi.createTransfer({
        route,
        sourceLocationId,
        targetLocationId: targetLocationId.trim(),
        // 展→展 不填去向会被服务端拒（422 TRANSFER_DESTINATION_REQUIRED）
        ...(route === 'EVENT_TO_EVENT' ? { transferDestination } : {}),
        lines: filled,
      });
      setShowTransferForm(false);
      setTransfers((await stockApi.listTransfers()).items);
    } catch (e) {
      setTransferError(readServerMessage(e) ?? '保存失败，请重试');
    } finally {
      setSavingTransfer(false);
    }
  };

  const runTransferAction = async (
    action: 'ship' | 'receive' | 'cancel',
    transfer: TransferView,
  ): Promise<void> => {
    if (action === 'cancel' && !window.confirm(`确定取消调拨单「${transfer.code}」吗？`)) return;
    try {
      if (action === 'ship') await stockApi.shipTransfer(transfer.id);
      if (action === 'receive') await stockApi.receiveTransfer(transfer.id);
      if (action === 'cancel') await stockApi.cancelTransfer(transfer.id);
      setTransfers((await stockApi.listTransfers()).items);
      setSummary(await stockApi.summary());
    } catch (e) {
      setError(readServerMessage(e) ?? '操作失败，请重试');
    }
  };

  const sourceOptions = locations.filter((location) =>
    ROUTE_LOCATION_TYPES[route].source.includes(location.type),
  );
  const targetOptions = locations.filter((location) =>
    ROUTE_LOCATION_TYPES[route].target.includes(location.type),
  );

  return (
    <div>
      <h1 className="mb-6 text-2xl font-bold text-gray-900">库存</h1>

      {loading && <p className="py-8 text-center text-gray-500">加载中…</p>}
      {!loading && error && <p className="font-bold text-red-600">{error}</p>}

      {!loading && !error && (
        <>
          <section className="mb-10">
            <h2 className="mb-3 text-lg font-bold text-gray-900">库存概览</h2>
            {summary.length === 0 ? (
              <p className="text-gray-500">该社团暂无库存记录</p>
            ) : (
              <table className="w-full rounded-lg border-2 border-black">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="border-b-2 border-black px-4 py-3 text-left font-bold">地点</th>
                    <th className="border-b-2 border-black px-4 py-3 text-left font-bold">类型</th>
                    <th className="border-b-2 border-black px-4 py-3 text-left font-bold">种类数</th>
                    <th className="border-b-2 border-black px-4 py-3 text-left font-bold">可用</th>
                    <th className="border-b-2 border-black px-4 py-3 text-left font-bold">预占</th>
                    <th className="border-b-2 border-black px-4 py-3 text-left font-bold">已售</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.map((row) => (
                    <tr key={row.locationId}>
                      <td className="border-b border-gray-200 px-4 py-3 text-sm">
                        {row.locationName}
                      </td>
                      <td className="border-b border-gray-200 px-4 py-3 text-sm">
                        {locationTypeLabel(row.type)}
                      </td>
                      <td className="border-b border-gray-200 px-4 py-3 text-sm">
                        {row.itemKindCount} 种
                      </td>
                      <td className="border-b border-gray-200 px-4 py-3 text-sm">
                        {row.totalAvailableQty} 件
                      </td>
                      <td className="border-b border-gray-200 px-4 py-3 text-sm">
                        {row.totalReservedQty} 件
                      </td>
                      <td className="border-b border-gray-200 px-4 py-3 text-sm">
                        {row.totalSoldQty} 件
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>

          <section className="mb-10">
            <h2 className="mb-3 text-lg font-bold text-gray-900">库存调整</h2>
            <div className="flex flex-col gap-3 rounded-lg border-2 border-black p-4">
              {adjustError && (
                <p className="rounded-lg border-2 border-red-500 bg-red-50 p-2 text-sm text-red-600">
                  {adjustError}
                </p>
              )}
              {notice && (
                <p className="rounded-lg border-2 border-black bg-gray-50 p-2 text-sm">{notice}</p>
              )}

              <div className="flex flex-wrap items-end gap-3">
                <label className="flex flex-col gap-1">
                  <span className="text-sm font-bold">地点</span>
                  <select
                    value={adjustLocationId}
                    onChange={(e) => setAdjustLocationId(e.target.value)}
                    className="min-h-[44px] rounded-lg border-2 border-black bg-white px-3 text-base outline-none focus:ring-2 focus:ring-black"
                  >
                    <option value="">请选择地点</option>
                    {locations.map((location) => (
                      <option key={location.id} value={location.id}>
                        {location.name}（{LOCATION_TYPE_LABELS[location.type]}）
                      </option>
                    ))}
                  </select>
                </label>

                <label className="flex flex-col gap-1">
                  <span className="text-sm font-bold">商品</span>
                  <select
                    value={adjustProductId}
                    onChange={(e) => setAdjustProductId(e.target.value)}
                    className="min-h-[44px] rounded-lg border-2 border-black bg-white px-3 text-base outline-none focus:ring-2 focus:ring-black"
                  >
                    <option value="">请选择商品</option>
                    {products.map((product) => (
                      <option key={product.id} value={product.id}>
                        {product.name}
                      </option>
                    ))}
                  </select>
                </label>

                <Input
                  label="数量（正数入库，负数出库）"
                  className="w-56"
                  inputMode="numeric"
                  value={adjustQuantity}
                  onChange={(e) => setAdjustQuantity(e.target.value)}
                />

                <Input
                  label="原因"
                  className="w-72"
                  value={adjustReason}
                  onChange={(e) => setAdjustReason(e.target.value)}
                />

                <Button
                  onClick={() => void submitAdjust()}
                  disabled={!canAdjust || adjusting}
                  className="disabled:opacity-50"
                >
                  {adjusting ? '提交中…' : '提交调整'}
                </Button>
              </div>

              {currentAvailable !== null && (
                <p className="text-sm text-gray-600">当前可用 {currentAvailable} 件</p>
              )}
            </div>
          </section>

          <section>
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-900">库存调拨</h2>
              {canTransfer && <Button onClick={openTransferForm}>+ 新建调拨</Button>}
            </div>

            {transfers.length === 0 ? (
              <p className="text-gray-500">暂无调拨单，点击「新建调拨」创建</p>
            ) : (
              <table className="w-full rounded-lg border-2 border-black">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="border-b-2 border-black px-4 py-3 text-left font-bold">编号</th>
                    <th className="border-b-2 border-black px-4 py-3 text-left font-bold">路线</th>
                    <th className="border-b-2 border-black px-4 py-3 text-left font-bold">源</th>
                    <th className="border-b-2 border-black px-4 py-3 text-left font-bold">目标</th>
                    <th className="border-b-2 border-black px-4 py-3 text-left font-bold">状态</th>
                    {canTransfer && (
                      <th className="border-b-2 border-black px-4 py-3 text-left font-bold">操作</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {transfers.map((transfer) => (
                    <tr key={transfer.id}>
                      <td className="border-b border-gray-200 px-4 py-3 text-sm">{transfer.code}</td>
                      <td className="border-b border-gray-200 px-4 py-3 text-sm">
                        {ROUTE_LABELS[transfer.route]}
                      </td>
                      <td className="border-b border-gray-200 px-4 py-3 text-sm">
                        {transfer.sourceLocation.name}
                      </td>
                      <td className="border-b border-gray-200 px-4 py-3 text-sm">
                        {transfer.targetLocation.name}
                      </td>
                      <td className="border-b border-gray-200 px-4 py-3 text-sm">
                        {TRANSFER_STATUS_LABELS[transfer.status]}
                      </td>
                      {canTransfer && (
                        <td className="border-b border-gray-200 px-4 py-3 text-sm">
                          <div className="flex gap-3">
                            {transfer.status === 'DRAFT' && (
                              <>
                                <button
                                  onClick={() => void runTransferAction('ship', transfer)}
                                  className="font-medium text-blue-600 hover:text-blue-800"
                                >
                                  发运
                                </button>
                                <button
                                  onClick={() => void runTransferAction('cancel', transfer)}
                                  className="font-medium text-red-600 hover:text-red-800"
                                >
                                  取消
                                </button>
                              </>
                            )}
                            {transfer.status === 'IN_TRANSIT' && (
                              <button
                                onClick={() => void runTransferAction('receive', transfer)}
                                className="font-medium text-blue-600 hover:text-blue-800"
                              >
                                接收
                              </button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        </>
      )}

      <Modal
        open={showTransferForm}
        onClose={() => setShowTransferForm(false)}
        title="新建调拨"
        maxWidth="max-w-2xl"
      >
        <div className="flex flex-col gap-3">
          {transferError && (
            <p className="rounded-lg border-2 border-red-500 bg-red-50 p-2 text-sm text-red-600">
              {transferError}
            </p>
          )}

          <label className="flex flex-col gap-1">
            <span className="text-sm font-bold">路线</span>
            <select
              value={route}
              onChange={(e) => {
                setRoute(e.target.value as StockTransferRoute);
                setSourceLocationId('');
                setTargetLocationId('');
              }}
              className="min-h-[44px] rounded-lg border-2 border-black bg-white px-3 text-base outline-none focus:ring-2 focus:ring-black"
            >
              {/*
                `EVENT_TO_WAREHOUSE` 是**系统专用**路线（展转去向 / `settle-destination` 生成），
                服务端 `POST /stock/transfers` 对它显式拒绝（`400 TRANSFER_INVALID_ROUTE`，A-P7-29）
                ⇒ 下拉里排掉它，用户不会选到一个必然失败的值。枚举本身仍含这一项（`OrderChain` 要用）。
              */}
              {STOCK_TRANSFER_ROUTES.filter((value) => value !== 'EVENT_TO_WAREHOUSE').map((value) => (
                <option key={value} value={value}>
                  {ROUTE_LABELS[value]}
                </option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-sm font-bold">源地点</span>
            <select
              value={sourceLocationId}
              onChange={(e) => setSourceLocationId(e.target.value)}
              className="min-h-[44px] rounded-lg border-2 border-black bg-white px-3 text-base outline-none focus:ring-2 focus:ring-black"
            >
              <option value="">请选择地点</option>
              {sourceOptions.map((location) => (
                <option key={location.id} value={location.id}>
                  {location.name}（{LOCATION_TYPE_LABELS[location.type]}）
                </option>
              ))}
            </select>
          </label>

          {route === 'TENANT_TO_TENANT' ? (
            // 对方社团的地点不在本社团的 `GET /locations` 结果里，只能手填地点 ID
            <Input
              label="目标地点 ID（对方社团的地点）"
              value={targetLocationId}
              onChange={(e) => setTargetLocationId(e.target.value)}
            />
          ) : (
            <label className="flex flex-col gap-1">
              <span className="text-sm font-bold">目标地点</span>
              <select
                value={targetLocationId}
                onChange={(e) => setTargetLocationId(e.target.value)}
                className="min-h-[44px] rounded-lg border-2 border-black bg-white px-3 text-base outline-none focus:ring-2 focus:ring-black"
              >
                <option value="">请选择地点</option>
                {targetOptions.map((location) => (
                  <option key={location.id} value={location.id}>
                    {location.name}（{LOCATION_TYPE_LABELS[location.type]}）
                  </option>
                ))}
              </select>
            </label>
          )}

          {route === 'EVENT_TO_EVENT' && (
            <label className="flex flex-col gap-1">
              <span className="text-sm font-bold">展转去向</span>
              <select
                value={transferDestination}
                onChange={(e) => setTransferDestination(e.target.value as TransferDestination)}
                className="min-h-[44px] rounded-lg border-2 border-black bg-white px-3 text-base outline-none focus:ring-2 focus:ring-black"
              >
                <option value="RETURN_TO_WAREHOUSE">回仓</option>
                <option value="CARRY_TO_NEXT_EVENT">结转下一场</option>
              </select>
            </label>
          )}

          <div className="flex flex-col gap-2">
            <span className="text-sm font-bold">调拨明细</span>
            {lines.map((line, index) => (
              <div key={index} className="flex items-center gap-3">
                <select
                  value={line.productId}
                  onChange={(e) =>
                    setLines((prev) =>
                      prev.map((row, i) => (i === index ? { ...row, productId: e.target.value } : row)),
                    )
                  }
                  className="min-h-[44px] flex-1 rounded-lg border-2 border-black bg-white px-3 text-base outline-none focus:ring-2 focus:ring-black"
                >
                  <option value="">请选择商品</option>
                  {products.map((product) => (
                    <option key={product.id} value={product.id}>
                      {product.name}
                    </option>
                  ))}
                </select>
                <input
                  type="number"
                  min={1}
                  value={line.quantity}
                  onChange={(e) =>
                    setLines((prev) =>
                      prev.map((row, i) =>
                        i === index ? { ...row, quantity: Number(e.target.value) } : row,
                      ),
                    )
                  }
                  className="min-h-[44px] w-24 rounded-lg border-2 border-black bg-white px-3 text-center text-base outline-none focus:ring-2 focus:ring-black"
                />
                <button
                  onClick={() => setLines((prev) => prev.filter((_, i) => i !== index))}
                  disabled={lines.length <= 1}
                  className="font-medium text-red-600 hover:text-red-800 disabled:opacity-30"
                >
                  移除
                </button>
              </div>
            ))}
            <div>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setLines((prev) => [...prev, { ...EMPTY_LINE }])}
              >
                添加商品
              </Button>
            </div>
          </div>

          <div className="mt-2 flex justify-end gap-3">
            <Button
              variant="secondary"
              onClick={() => setShowTransferForm(false)}
              disabled={savingTransfer}
            >
              取消
            </Button>
            <Button
              onClick={() => void submitTransfer()}
              disabled={savingTransfer}
              className="disabled:opacity-50"
            >
              {savingTransfer ? '提交中…' : '确认'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
